<template>
  <v-card class="mb-8" elevation="0">
    <v-card-text>
      <div class="subtitle-1 font-weight-bold mb-4">
        <v-icon left>mdi-store</v-icon>
        {{ $t("restaurant_selection") }}
      </div>

      <v-row>
        <!-- Pool / Individual toggle -->
        <v-col cols="12">
          <div class="panel-label mb-2">
            <v-icon small left>mdi-lightning-bolt-circle</v-icon>
            {{ $t("restaurant_limit_mode_label") }}
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-card
                outlined
                class="mode-card pa-3"
                :class="{ 'mode-card--active': limit.scope_type === 'pool' }"
                @click="!readonly && (limit.scope_type = 'pool')"
                :ripple="!readonly"
              >
                <div class="d-flex align-center">
                  <v-icon
                    left
                    :color="limit.scope_type === 'pool' ? 'deep-purple' : 'grey'"
                  >
                    mdi-pool
                  </v-icon>
                  <div>
                    <div class="font-weight-bold">
                      {{ $t("restaurant_limit_mode_pool") }}
                    </div>
                    <div class="caption grey--text">
                      {{ $t("restaurant_limit_mode_pool_desc") }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-icon v-if="limit.scope_type === 'pool'" color="deep-purple"
                    >mdi-check-circle</v-icon
                  >
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card
                outlined
                class="mode-card pa-3"
                :class="{ 'mode-card--active': limit.scope_type === 'individual' }"
                @click="!readonly && (limit.scope_type = 'individual')"
                :ripple="!readonly"
              >
                <div class="d-flex align-center">
                  <v-icon
                    left
                    :color="limit.scope_type === 'individual' ? 'deep-purple' : 'grey'"
                  >
                    mdi-store-outline
                  </v-icon>
                  <div>
                    <div class="font-weight-bold">
                      {{ $t("restaurant_limit_mode_individual") }}
                    </div>
                    <div class="caption grey--text">
                      {{ $t("restaurant_limit_mode_individual_desc") }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-icon v-if="limit.scope_type === 'individual'" color="deep-purple"
                    >mdi-check-circle</v-icon
                  >
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <!-- LEFT: Available restaurants -->
        <v-col cols="12" md="6">
          <div class="panel-label mb-2">
            <v-icon small left>mdi-store-search</v-icon>
            {{ $t("available_restaurants") }}
          </div>

          <v-card outlined class="selection-panel">
            <v-card-text class="pa-2">
              <v-text-field
                v-model="search"
                :placeholder="$t('search')"
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                hide-details
                class="mb-2"
                clearable
              />
              <v-progress-linear v-if="loading" indeterminate color="deep-purple" />

              <v-list dense class="available-list">
                <v-list-item
                  v-for="restaurant in filteredAvailable"
                  :key="restaurant.id"
                  @click="addRestaurant(restaurant)"
                  :disabled="readonly"
                  class="restaurant-item"
                >
                  <v-list-item-avatar size="32">
                    <v-img
                      v-if="restaurant.settings && restaurant.settings.logo"
                      :src="restaurant.settings.logo"
                      contain
                    />
                    <v-icon v-else small>mdi-store</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ toLatin(restaurant.name) }}</v-list-item-title>
                    <v-list-item-subtitle v-if="restaurant.city">
                      {{ toLatin(restaurant.city.name) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small color="deep-purple" :disabled="readonly">
                      <v-icon small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="!loading && filteredAvailable.length === 0">
                  <v-list-item-content class="text-center grey--text">
                    {{ $t("no_restaurants_found") }}
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT: Selected restaurants -->
        <v-col cols="12" md="6">
          <div class="panel-label mb-2">
            <v-icon small left color="deep-purple">mdi-check-circle</v-icon>
            {{ $t("selected_restaurants") }}
            <v-chip x-small class="ml-2" color="deep-purple" dark>
              {{ selectedRestaurantObjects.length }}
            </v-chip>
          </div>

          <v-card outlined class="selection-panel selected-panel">
            <v-card-text class="pa-2">
              <v-list dense class="selected-list">
                <v-list-item
                  v-for="restaurant in selectedRestaurantObjects"
                  :key="restaurant.id"
                  @click="removeRestaurant(restaurant)"
                  :disabled="readonly"
                  class="restaurant-item selected-restaurant-item"
                >
                  <v-list-item-avatar size="32">
                    <v-img
                      v-if="restaurant.settings && restaurant.settings.logo"
                      :src="restaurant.settings.logo"
                      contain
                    />
                    <v-icon v-else small color="deep-purple">mdi-store</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ toLatin(restaurant.name) }}</v-list-item-title>
                    <v-list-item-subtitle v-if="restaurant.city">
                      {{ toLatin(restaurant.city.name) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small color="error" :disabled="readonly">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="selectedRestaurantObjects.length === 0">
                  <v-list-item-content>
                    <div class="text-center grey--text pa-4">
                      <v-icon large class="mb-2" color="grey lighten-1"
                        >mdi-store-off</v-icon
                      >
                      <div>{{ $t("no_restaurants_selected") }}</div>
                      <div class="caption mt-1">{{ $t("click_restaurant_to_add") }}</div>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <div
            v-if="selectedRestaurantObjects.length > 0 && !readonly"
            class="mt-2 text-right"
          >
            <v-btn text x-small color="error" @click="clearAll">
              <v-icon x-small left>mdi-trash-can-outline</v-icon>
              {{ $t("clear_all") }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import companyService from "@/services/company";

export default {
  name: "Step5RestaurantsSelection",
  props: {
    config: {
      type: Object,
      required: true,
    },
    limit: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allRestaurants: [],
      loading: false,
      search: "",
    };
  },
  computed: {
    selectedRestaurantObjects() {
      return Array.isArray(this.limit.restaurants) ? this.limit.restaurants : [];
    },
    availableRestaurants() {
      return this.allRestaurants;
    },
    filteredAvailable() {
      if (!this.search) return this.availableRestaurants;
      const q = this.search.toLowerCase();
      return this.availableRestaurants.filter(
        (r) =>
          (r.name && r.name.toLowerCase().includes(q)) ||
          (r.city && r.city.name && r.city.name.toLowerCase().includes(q))
      );
    },
  },
  watch: {
    "limit.company_id": {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        if (!Array.isArray(this.limit.restaurants)) {
          this.$set(this.limit, "restaurants", []);
        }
        if (!this.limit.scope_type) {
          this.$set(this.limit, "scope_type", "pool");
        }
        this.fetchRestaurants();
      },
    },
  },
  methods: {
    async fetchRestaurants() {
      if (!this.limit?.company_id) return;
      this.loading = true;
      try {
        const response = await companyService.getCompanyRestaurants(
          this.limit.company_id
        );
        this.allRestaurants = response.data;
      } catch (error) {
        console.error("Error fetching restaurants:", error);
        this.allRestaurants = [];
      } finally {
        this.loading = false;
      }
    },
    addRestaurant(restaurant) {
      if (this.readonly) return;
      const already = this.selectedRestaurantObjects.some((r) => r.id === restaurant.id);
      if (!already) {
        this.limit.restaurants = [
          ...this.selectedRestaurantObjects,
          { id: restaurant.id, name: restaurant.name },
        ];
      }
    },
    removeRestaurant(restaurant) {
      if (this.readonly) return;
      this.limit.restaurants = this.selectedRestaurantObjects.filter(
        (r) => r.id !== restaurant.id
      );
    },
    clearAll() {
      if (this.readonly) return;
      this.limit.restaurants = [];
    },
  },
};
</script>

<style scoped>
.mode-card {
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  border-radius: 8px !important;
}

.mode-card--active {
  border-color: #673ab7 !important;
  background-color: rgba(103, 58, 183, 0.05);
}

.panel-label {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.6);
}

.selection-panel {
  min-height: 340px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
}

.available-list,
.selected-list {
  max-height: 280px;
  overflow-y: auto;
}

.restaurant-item {
  border-radius: 6px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.restaurant-item:hover:not(.v-list-item--disabled) {
  background-color: rgba(103, 58, 183, 0.08);
}

.selected-panel {
  border-color: rgba(103, 58, 183, 0.4) !important;
}

.selected-restaurant-item:hover:not(.v-list-item--disabled) {
  background-color: rgba(244, 67, 54, 0.07);
}
</style>
